import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Autocomplete } from '@mui/material';
import ModalActionBar from 'components/modal-action-bar';
import TextField from 'components/text-field';
import { GQLCreatePartyAssociation } from 'graphql/parties/mutations';
import React, { FC } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { KindField } from './components/kindField';
import { useModalCreateAssociationHooks } from './hooks';
import { useStyles } from './styles';
import useContactsVariant, { resolveDialogFullScreen } from 'views/contacts/hooks/use-contacts-variant';
import { RadioButtonForm } from 'views/orders/loans/components/terms-and-dates-increases-disclosure/components/disclosures/components/radio-button-form';
import {
  CONTACT_TITLE_AGENT,
  CONTACT_TITLE_ADMINISTRATOR,
  CONTACT_TITLE_AGENCY
} from 'utils/constants';
import { autocompleteRootProps } from 'v2-utils/autocomplete';

type ModalCreateAssociationProps = {
  partyId: string,
  isTitleCompany: boolean,
  isTitleAgency: boolean,
  open: boolean,
  onSuccess: (data: GQLCreatePartyAssociation) => void,
  onClose: () => void,
  onError: () => void,
};

const titleRoles = [
  { code: CONTACT_TITLE_AGENT, description: CONTACT_TITLE_AGENT },
  { code: CONTACT_TITLE_ADMINISTRATOR, description: CONTACT_TITLE_ADMINISTRATOR },
  { code: CONTACT_TITLE_AGENCY, description: CONTACT_TITLE_AGENCY },
];

export const ModalCreateAssociation: FC<ModalCreateAssociationProps> = ({
  partyId,
  isTitleCompany,
  isTitleAgency,
  open,
  onSuccess,
  onClose,
  onError
}) => {
  const classes = useStyles();
  const {
    reset,
    control,
    createAssociation,
    parties,
    setSearchText,
    setValue,
    errors,
    t,
    isValid,
    allowedAssociations,
    getValues,
    isSaving
  } = useModalCreateAssociationHooks({ partyId, onSuccess, onClose, onError });

  const fullScreen = resolveDialogFullScreen(useContactsVariant());

  // Mutation in flight = modal locked (DESIGN.md §5).
  const handleOnClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  const typeOfRelation = useWatch({ control, name: "type" });
  const currentContact = useWatch({ control, name: "selected" });

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      disableEscapeKeyDown={isSaving}
    >
      <DialogTitle className={classes.title}>
        {t('parties:create-assoc')}
      </DialogTitle>

      <DialogContent>
        <form onSubmit={(e) => {
          e.preventDefault();
          createAssociation();
        }}
        >
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="type"
                render={({ field }) => (
                  <RadioButtonForm
                    value={field.value}
                    options={[
                      {
                        label: 'Subordinate',
                        disabled: false,
                        value: 'member of'
                      },
                      {
                        label: 'Member Of',
                        disabled: false,
                        value: 'subordinate'
                      }
                    ]}
                    onChange={(value) => field.onChange(value)}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={12} sm={8}>
                  <Controller
                    name="selected"
                    control={control}
                    render={({ field: { ref, onChange, ...field } }) => (
                      <Autocomplete
                        {...field}
                        freeSolo
                        fullWidth
                        disabled={isSaving}
                        ref={ref}
                        options={parties}
                        filterOptions={(options) => options}
                        getOptionLabel={(party) => (typeof party === "string" ? party : party.name)}
                        onInputChange={(_, value, reason) => {
                          if (reason === 'input') {
                            setSearchText(value);
                          }
                        }}
                        onChange={(_, value) => {
                          if (value && typeof value === 'object') {
                            onChange(value);
                            setValue('_id', value._id);
                            setValue('name', value.name);
                            const istitleCompanyContact = value?.kind.includes('Title Company');
                            const currentKind = getValues('kind');
                            if (!istitleCompanyContact && titleRoles.some(f => f.code === currentKind)) {
                              setValue('kind', allowedAssociations[allowedAssociations.length - 1]?.code || "");
                            }
                          } else {
                            onChange(null);
                            setValue('_id', '');
                            setValue('name', '');
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            inputRef={params.InputProps.ref}
                            InputProps={autocompleteRootProps(params.inputProps)}
                            label={t('users:name')}
                            error={!!errors._id}
                            helperText={
                              errors._id?.message || t('validations:required')
                            }
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <KindField
                    isTitleCompany={isTitleCompany || (!!currentContact?.kind.includes('Title Company') && typeOfRelation !== 'member of')}
                    isTitleAgency={isTitleAgency || (!!currentContact?.kind.includes('title Agency') && typeOfRelation !== 'member of')}
                    control={control}
                    errors={errors}
                    listingEntries={allowedAssociations}
                    disabled={isSaving}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <ModalActionBar
        onCancel={handleOnClose}
        submitType="button"
        submitText={t('users:create')}
        onSubmit={createAssociation}
        isSubmitting={isSaving}
        submitDisabled={!isValid}
      />
    </Dialog>
  );
};
